import React, { useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  CssBaseline,
  ThemeProvider,
  StyledEngineProvider
} from '@mui/material';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import { QueryClientProvider } from '@tanstack/react-query';
import withErrorBoundary from 'src/hocs/withErrorBoundary';

import 'simplebar-react/dist/simplebar.min.css';
import 'jsoneditor-react/es/editor.min.css';
import 'react-image-gallery/styles/css/image-gallery.css';
import 'react-json-pretty/themes/1337.css';
import 'src/styles/reactslider-ovverides.css';
import 'src/styles/ckeditor-override.css';
import 'src/styles/simplebar-scrollbar-override.css';

import ZendeskWidget from 'src/components/widgets/ZendeskWidget';
import combineComponents from 'src/utils/combineComponents';
import { SessionProvider } from 'src/contexts/SessionContext';
import { ProgressProvider } from 'src/contexts/ProgressContext';
import { AlertProvider } from 'src/contexts/AlertContext';
import { IndustriesProvider } from 'src/contexts/IndustriesContext';
import { InstancesProvider } from 'src/contexts/InstancesContext';
import { WidgetProvider } from 'src/contexts/WidgetContext';
import GlobalStyles from 'src/components/GlobalStyles';
import NetworkDetector from 'src/components/widgets/NetworkDetector';
import { createMuiTheme, CKEditorDarkTheme } from 'src/theme';
import oktaAuth from 'src/libraries/oktaAuth';
import { gtagEvent } from 'src/utils/gtag';
import queryClient from 'src/libraries/queryClient';
import useSettingsContext from 'src/hooks/useSettingsContext';
import GlobalProgressBar from 'src/components/widgets/progress/LinearProgressBar';

const providers = [
  AlertProvider,
  WidgetProvider,
  ProgressProvider,
  IndustriesProvider,
  InstancesProvider
];
const ContextProviders = combineComponents(...providers);

const App = () => {
  const { settings } = useSettingsContext();

  const navigate = useNavigate();
  const location = useLocation();
  const restoreOriginalUri = async () => {
    navigate(toRelativeUrl('/login/consume', window.location.origin), {
      replace: true
    });
  };

  useEffect(() => {
    try {
      gtagEvent('page_view');
    } catch (e) {
      console.error(e);
    }
  }, [location]);

  const theme = createMuiTheme({
    theme: settings.theme
  });

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <GlobalStyles />
              <CssBaseline />
              <CKEditorDarkTheme />
              <ContextProviders>
                <GlobalProgressBar />
                <Outlet />
                <ZendeskWidget />
              </ContextProviders>
              <NetworkDetector />
            </ThemeProvider>
          </StyledEngineProvider>
        </SessionProvider>
      </QueryClientProvider>
    </Security>
  );
};

export default withErrorBoundary(App);
